<template>
  <div data-testid="mandate-sales-strategy">
    <fieldset class="mb-3">
      <legend class="form-label mb-1">
        {{ $t("forwards.sales_strategy.title") }}
        <InfoTooltip :text="$t('forwards.sales_strategy.help')" />
      </legend>
      <div
        v-for="strategy in SALES_STRATEGIES"
        :key="strategy"
        class="form-check"
      >
        <input
          :id="`${splitId}-${strategy}`"
          v-model="salesStrategy"
          class="form-check-input"
          type="radio"
          :name="splitId"
          :value="strategy"
          :disabled="disabled"
        />
        <label class="form-check-label" :for="`${splitId}-${strategy}`">
          {{ $t(`forwards.sales_strategy.split.${strategy}`) }}
          <span class="text-body-tertiary fs-10">
            — {{ $t(`forwards.sales_strategy.split_help.${strategy}`) }}
          </span>
        </label>
      </div>
    </fieldset>

    <div v-if="sellAhead" class="mb-3" data-testid="sell-ahead">
      <fieldset>
        <legend class="form-label mb-1">
          {{ $t("forwards.sales_strategy.sell_ahead.label") }}
          <InfoTooltip :text="$t('forwards.sales_strategy.sell_ahead.help')" />
        </legend>
        <div class="form-check">
          <input
            :id="`${sellAheadId}-next-year`"
            v-model="sellsAhead"
            class="form-check-input"
            type="radio"
            :name="sellAheadId"
            :value="false"
            :disabled="disabled"
            data-testid="sell-ahead-next-year"
          />
          <label class="form-check-label" :for="`${sellAheadId}-next-year`">
            {{ $t("forwards.sales_strategy.sell_ahead.next_year_only") }}
          </label>
        </div>
        <div class="form-check">
          <input
            :id="`${sellAheadId}-ahead`"
            v-model="sellsAhead"
            class="form-check-input"
            type="radio"
            :name="sellAheadId"
            :value="true"
            :disabled="disabled"
            data-testid="sell-ahead-two-years"
          />
          <label
            :id="`${sellAheadId}-ahead-label`"
            class="form-check-label"
            :for="`${sellAheadId}-ahead`"
          >
            {{ $t("forwards.sales_strategy.sell_ahead.two_years_ahead") }}
          </label>
          <div
            v-if="sellsAhead"
            class="d-flex flex-wrap align-items-center column-gap-2 row-gap-1 mt-1"
            data-testid="sell-ahead-shares"
          >
            <div
              class="btn-group btn-group-sm"
              role="group"
              :aria-labelledby="`${sellAheadId}-ahead-label`"
              :aria-describedby="`${sellAheadId}-share-of`"
            >
              <button
                v-for="share in AHEAD_SHARES"
                :key="share"
                type="button"
                class="btn"
                :class="
                  share === plan.early_share_pct
                    ? 'btn-primary'
                    : 'btn-outline-primary'
                "
                :aria-pressed="share === plan.early_share_pct"
                :disabled="disabled"
                :data-testid="`sell-ahead-${share}`"
                @click="setEarlyShare(share)"
              >
                {{
                  $t("forwards.sales_strategy.sell_ahead.preset", {
                    percent: share,
                  })
                }}
              </button>
            </div>
            <span
              :id="`${sellAheadId}-share-of`"
              class="text-body-secondary fs-10"
              data-testid="sell-ahead-share-of"
            >
              {{ $t("forwards.sales_strategy.sell_ahead.share_of") }}
            </span>
          </div>
        </div>
      </fieldset>

      <p class="fs-10 mt-2 mb-1" data-testid="sell-ahead-description">
        {{ description }}
      </p>
      <template v-if="plan.early_share_pct > 0">
        <div class="text-body-tertiary fs-10 mb-1">
          {{
            $t("forwards.sales_strategy.sell_ahead.bar_caption", {
              year: barYear,
            })
          }}
        </div>
        <div
          class="progress-stacked sell-ahead-bar"
          data-testid="sell-ahead-bar"
        >
          <div
            v-for="segment in barSegments"
            :key="segment.year"
            class="progress"
            :class="segment.widthClass"
            role="progressbar"
            :aria-valuenow="segment.percent"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div class="progress-bar" :class="segment.barClass">
              {{
                $t("forwards.sales_strategy.sell_ahead.bar_segment", {
                  year: segment.year,
                  percent: segment.percent,
                })
              }}
            </div>
          </div>
        </div>
      </template>
    </div>

    <p class="text-body-tertiary fs-10 mb-0">
      {{ $t("forwards.sales_strategy.future_only") }}
    </p>

    <p
      v-for="change in releaseChanges"
      :key="change.production_year"
      class="text-info-emphasis fs-10 mt-2 mb-0"
      role="status"
      data-testid="sales-strategy-release-change"
    >
      <FontAwesomeIcon icon="fa-regular fa-circle-info" class="me-1" />
      {{ releaseChangeText(change) }}
    </p>

    <p
      v-if="commitmentsStand"
      class="text-info-emphasis fs-10 mt-2 mb-0"
      role="status"
      data-testid="sales-strategy-commitments-stand"
    >
      <FontAwesomeIcon icon="fa-regular fa-circle-info" class="me-1" />
      {{ $t("forwards.sales_strategy.commitments_stand_inline") }}
    </p>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import {
  EARLY_SHARE_PRESETS,
  SALES_STRATEGIES,
  type CommittedAboveRelease,
  type ReleaseChange,
  type SalesPlan,
  type SalesStrategy,
} from "@/forwards2/forwardsTypes";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The mandate's sales strategy, as its producer sets it: the split, and where staff have shown the
 * advanced listing options, the early share sold two years ahead (`sellAhead`) with a sentence and
 * a bar saying what the choice means. The schedule the strategy releases the listing on opens from
 * the listing (`MandateSalesScheduleButtonComponent`).
 *
 * Producers see this, so it never mentions that staff can override the strategy.
 */
const props = withDefaults(
  defineProps<{
    /** The preview's periods committed above today's release; none without a preview. */
    committedAboveRelease?: CommittedAboveRelease[];
    /** What a save would change in today's release, per production year; none without a preview. */
    releaseChanges?: ReleaseChange[];
    /** Offer the early share: the advanced listing options are shown, or the mandate already sells ahead. */
    sellAhead?: boolean;
    disabled?: boolean;
  }>(),
  {
    committedAboveRelease: () => [],
    releaseChanges: () => [],
    sellAhead: false,
    disabled: false,
  }
);

const plan = defineModel<SalesPlan>({ required: true });

const { t } = useI18n();
const splitId = useId();
const sellAheadId = useId();

// Bootstrap's width utilities, which cover every preset share and its remainder.
const WIDTH_CLASSES: Record<number, string> = {
  25: "w-25",
  50: "w-50",
  75: "w-75",
  100: "w-100",
};

const AHEAD_SHARES = EARLY_SHARE_PRESETS.filter((share) => share > 0);

/** Shown beside the choices, where a stricter strategy is picked. */
const commitmentsStand = computed(() => props.committedAboveRelease.length > 0);

// Replaced, never mutated: the editor's form and its saved copy can hold the same plan object.
const salesStrategy = computed({
  get: () => plan.value.strategy,
  set: (strategy: SalesStrategy) => {
    plan.value = { ...plan.value, strategy };
  },
});

const sellsAhead = computed({
  get: () => plan.value.early_share_pct > 0,
  set: (ahead: boolean) => setEarlyShare(ahead ? AHEAD_SHARES[0] : 0),
});

const description = computed(() => {
  const share = plan.value.early_share_pct;
  const lead =
    share === 0
      ? t("forwards.sales_strategy.sell_ahead.lead_next_year")
      : t("forwards.sales_strategy.sell_ahead.lead_ahead", {
          early: share,
          rest: 100 - share,
        });
  const cadence = t(
    `forwards.sales_strategy.sell_ahead.${share === 0 ? "cadence" : "cadence_ahead"}.${plan.value.strategy}`
  );
  return `${lead} ${cadence}`;
});

/** The production year whose early share this sales year sells. */
const barYear = computed(() => new Date().getUTCFullYear() + 2);

const barSegments = computed(() => {
  const share = plan.value.early_share_pct;
  return [
    {
      year: barYear.value - 2,
      percent: share,
      barClass: "bg-primary-subtle text-body-emphasis",
    },
    { year: barYear.value - 1, percent: 100 - share, barClass: "bg-primary" },
  ]
    .filter((segment) => segment.percent > 0)
    .map((segment) => ({
      ...segment,
      widthClass: WIDTH_CLASSES[segment.percent] ?? "",
    }));
});

function setEarlyShare(share: number) {
  plan.value = { ...plan.value, early_share_pct: share };
}

function releaseChangeText(change: ReleaseChange): string {
  const values = {
    mwh: formatNumberWithSpaces(Math.abs(change.delta_mwh)),
    year: change.production_year,
  };
  return change.delta_mwh > 0
    ? t("forwards.sales_strategy.sell_ahead.release_more", values)
    : t("forwards.sales_strategy.sell_ahead.release_less", values);
}
</script>

<style scoped lang="scss">
// Tall enough for the segment's label, which the default progress height clips. The theme sets the
// height on each segment as well as on the stack, so both take it.
.sell-ahead-bar,
.sell-ahead-bar .progress {
  --phoenix-progress-height: 1.5rem;
}
</style>
